// WP-C1.2 (CAL-1..5, S-34): the merged agenda query.
import { agendaResponseSchema, type AgendaResponse } from '@pfos/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from './api';

// Every useAgenda query key is ['agenda', from, to, ...] — a prefix-only invalidate (no from/to)
// matches every open agenda window at once (react-query's default partial-key matching), which
// is what WP-C2.3's event-write mutations (lib/calendars.ts) need after a create/update/delete.
export const AGENDA_KEY = ['agenda'] as const;

export function useAgenda(from: string, to: string): UseQueryResult<AgendaResponse> {
  return useQuery({
    queryKey: [...AGENDA_KEY, from, to],
    queryFn: async () =>
      agendaResponseSchema.parse(
        await api.get(
          `/api/v1/agenda?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        ),
      ),
  });
}

/** WP-C2.3 (CW-1/CW-2): a successful create/update/delete against an OAuth calendar connection
 * upserts/removes the event in that connection's LOCAL cache immediately (see
 * oauthConnectionService.ts) — invalidating here just makes the Agenda page's own query refetch
 * and pick that up right away, instead of waiting for its next natural refetch. */
export function useInvalidateAgenda(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: AGENDA_KEY });
  };
}
